/**
 * Analyzify Builder - constructs the window.analyzify object
 *
 * Uses HeadlessConfig accessor to read raw metafield config directly
 * without any transformation.
 */

import type { PageContext } from '../types/config';
import type { WindowAnalyzify } from '../types/window-analyzify';
import type { HeadlessConfig } from './headless-config';
import { buildPlatformProperties } from './platform-builder';
import { DEFAULTS } from './constants';
import { buildAnalyzifyConsentDetail } from '../consent/consent-manager';

/**
 * Shop context options for building the analyzify object
 */
export interface BuilderShopOptions {
  shopDomain: string;
  locale?: string;
  country?: string;
  currency?: string;
}

/**
 * Build the complete window.analyzify object
 *
 * @param config - HeadlessConfig accessor instance
 * @param pageContext - Current page context
 * @param shopOptions - Shop context options (domain, locale, etc.)
 * @returns WindowAnalyzify object matching what Liquid templates produce
 */
export function buildAnalyzifyObject(
  config: HeadlessConfig,
  pageContext: PageContext,
  shopOptions?: BuilderShopOptions
): WindowAnalyzify {
  const properties = buildPlatformProperties(config);
  const currency = shopOptions?.currency || config.str('currency') || DEFAULTS.currency;
  const feedCountry = (config.str('gads_feed_country') || config.str('gads_feed_region') || DEFAULTS.country).toUpperCase();
  const feedRegion = (config.str('gads_feed_region') || DEFAULTS.country).toUpperCase();

  return {
    // Platform properties
    properties,

    // Version
    analyzify_version: '4.3.40.58',

    // Logging & Debug
    logging: config.has('analyzify.logging') ? config.bool('analyzify.logging') : config.bool('logging'),
    logs: [],
    stopAtLog: false,
    testing_environment: false,
    debug: config.bool('debug'),
    debug_mode: config.bool('debug'),

    // Initialization tracking
    initial_load: {
      initialize: false,
      app_start: false,
      consent_ready: false,
    },

    // Shop & Template info
    shopify_template: pageContext.type,
    content_type: pageContext.type.charAt(0).toUpperCase() + pageContext.type.slice(1),

    // Same as the Analyzify app configuration: the myshopify URL (Shopify.shop)
    // and the shop handle. Reported on sh_info and every GA4/Ads config.
    storeObj: buildStoreObj(shopOptions?.shopDomain),

    market: {
      id: '',
      handle: shopOptions?.locale || 'en',
      language: shopOptions?.locale || 'en',
      country: shopOptions?.country || feedCountry,
    },

    shop: {
      root_url: '/',
      locale: shopOptions?.locale || 'en',
      country: shopOptions?.country || feedCountry,
    },

    currency,

    // DOM attributes for element detection (used by CDN scripts)
    attributes: {
      variant_options: {
        class: [
          'variant-radios',
          'variant-picker',
          '.product-form__input',
          '.variant-selects',
          '.product-form__variants',
        ],
      },
    },

    addtocart_btn_attributes: {
      type: ['submit'],
      name: ['add-to-cart', 'add'],
      'data-add-to-cart-text': ['Add to Cart'],
      class: [
        'addtocart-button',
        'pdp-form--atc-button',
        'button-add',
        'add-to-cart',
        'add_to_cart',
        'buttonAddtoCart',
        'product-form__add-to-cart',
        'gtmatc',
        'product-form__cart-submit',
        'AddToCartText',
        'AddToCart',
        'AddToCart-product-template',
        'product__add-to-cart',
        'single_add_to_cart_button',
        'js_frm_cart',
        'product-buy-buttons--cta',
        'jsfrmcart',
      ],
      id: ['AddToCart'],
    },

    wishlist_btn_attributes: {
      class: ['test-wishlist', 'wishlist_button'],
      name: ['wishlist'],
      'aria-label': ['In Wishlist'],
    },

    product_quantity: {
      name: ['quantity', 'updates[]'],
      class: ['quantity-selector__input'],
    },

    removefromcart_btn_attributes: {
      'data-remove-item': ['cart-template'],
      'data-cart-remove': ['Remove'],
      'aria-label': ['Remove'],
      class: [
        'cart__remove-btn',
        'cart__remove',
        'cart__removee',
        'cart-item__remove',
        'item-remove',
        'remove',
        'rebuy-cart__flyout-item-remove',
        'cart_ac_remove',
        'cartacremove',
        'previewCartItem-remove',
        'cart-remove',
        'btn-remove',
        'remove-product',
        'ajaxcart__qty-remove',
        'quick-cart__item-remove',
        'cart-remove-line',
      ],
      id: ['CartDrawer-Remove'],
      href: ['/cart/change?id=', '/cart/change?line='],
    },

    checkout_btn_attributes: {
      name: ['checkout'],
      class: [
        'upcart-checkout-button',
        'cart__submit',
        'checkout-trigger',
        'rebuy-cart__checkout-button',
        'button-checkout',
        'checkout-btn',
        'cart__checkout-button',
      ],
      href: ['/checkout'],
      id: ['CartDrawer-Checkout', 'checkout'],
      value: ['Checkout'],
    },

    collection_prod_click_attributes: {
      href: ['/products/'],
      class: ['boost-pfs-addtocart-select-options'],
    },

    collection_atc_attributes: {
      name: ['add'],
      class: [
        'add-to-cart-btn',
        'hit-buy-button',
        'product-form__cart-submit',
        'spf-product__form-btn-addtocart',
        'add-to-cart',
        'boost-pfs-addtocart-btn',
        'js_addtc',
        'pratc',
      ],
      type: ['submit'],
      'aria-label': ['Add to cart'],
      id: ['product-add-to-cart'],
    },

    search_prod_click_attributes: {
      href: ['/products/'],
    },

    header_nav_btn_attributes: {
      class: ['header-shortlink', 'header__menu-item', 'nav-bar__link'],
      id: [],
    },

    disclosure_attributes: {
      class: ['disclosure__link'],
    },

    accordion_summary_attributes: {
      class: ['accordion__title', 'accordion'],
    },

    hero_banner_area_attributes: {
      class: [
        'banner__box',
        'banner__column-inner banner__column-inner--hero banner__column-inner--hero-large',
      ],
    },

    hero_banner_title_attributes: {
      class: [
        'banner__heading',
        'content__title content__title--hero content__title--hero-large',
      ],
    },

    hero_banner_subtitle_attributes: {
      class: [
        'content__subtitle content__subtitle--hero content__subtitle--hero-large',
      ],
    },

    hero_banner_cta_attributes: {
      class: [
        'content__buttons content__buttons--hero content__buttons--hero-large',
      ],
    },

    general_atc_btn_attributes: {
      class: ['Sd_addProduct'],
    },

    global_atc_functions: ['pplrAddToCartCompleted'],
    foundElements: [],
    foundAtcElementForms: [],
    foundBoostElements: [],

    // Customer (visitor by default)
    shopify_customer: {
      type: 'visitor',
    },

    // Cart
    detectedCart: {
      items: [],
      total_price: 0,
      item_count: 0,
      token: '',
      currency,
    },
    cart_id: null,

    // Configuration flags
    variant_changed_with_view_item: config.bool('variant_changed_with_view_item'),
    chunk_size: config.int('items_chunksize') || DEFAULTS.chunkSize,
    send_unhashed_email: config.bool('unhashed_email'),
    feed_region: feedRegion,
    feed_country: feedCountry,
    user_id_format: config.str('user_id_format') || DEFAULTS.userIdFormat,
    send_user_id: config.has('send_user_id') ? config.bool('send_user_id') : DEFAULTS.sendUserId,

    // Third-party integrations
    rebuy_atc_rfc: config.bool('rebuy_atc_rfc'),
    pageFly: config.bool('pageFly'),
    shogun_active: config.bool('shogun'),

    // Data collection
    op_cart_data_collection: config.bool('op_cart_data_collection'),
    checksendcartdata_status: false,
    _isSameDomainNavigation: false,

    // User data
    hide_raw_userdata: config.bool('hide_raw_userdata'),

    // Cart attribute reporting
    report_cart_atr_upsert: config.has('cart_atr_upsert') ? config.bool('cart_atr_upsert') : true,
    report_cart_atr_admin: config.has('cart_atr_admin') ? config.bool('cart_atr_admin') : true,
    report_cart_atr_admin_utm: config.has('report_cart_atr_admin_utm') ? config.bool('report_cart_atr_admin_utm') : true,
    report_cart_atr_admin_cook: config.has('report_cart_atr_admin_cook') ? config.bool('report_cart_atr_admin_cook') : true,
    report_cart_atr_admin_clid: config.has('report_cart_atr_admin_clid') ? config.bool('report_cart_atr_admin_clid') : true,
    report_cart_atr_admin_consent: config.has('report_cart_atr_admin_consent') ? config.bool('report_cart_atr_admin_consent') : true,

    // Consent management
    consent_active: config.bool('consent_active'),
    gcm_active: config.bool('gcm_active'),
    cookiebot_active: config.bool('cookiebot_active'),
    cookiebot_id: config.str('cookiebot_id'),
    // Basic vs advanced consent mode (the Analyzify app configuration: default false).
    // an_ga4_gads.js branches on `basic_consent_mode_active === false`; left
    // undefined it takes the basic branch and never configures GA4/Ads for a
    // visitor who grants consent after page load.
    basic_consent_mode_active: config.bool('basic_consent_mode_active'),
    ga4_basic_consent_mode_active: config.bool('ga4_basic_consent_mode_active'),
    gads_basic_consent_mode_active: config.bool('gads_basic_consent_mode_active'),

    consent_flag: {
      default: false,
      update: false,
      initial: false, // Extension always defaults to false
    },

    current_consent: {
      ad_storage: null,
      analytics_storage: null,
      ad_user_data: null,
      ad_personalization: null,
      personalization_storage: null,
      functionality_storage: 'granted',
      security_storage: 'granted', // Required by extension
      sale_of_data: null, // CCPA - California
      wait_for_update: DEFAULTS.consentWaitForUpdate,
    },

    consent_version: config.bool('consent_active') ? '2.4.1' : null,

    // ConsentManager - queue system for consent-dependent operations
    // Used by CDN scripts to queue actions until consent is ready
    consentManager: config.bool('consent_active') ? createConsentManager() : undefined,

    // Storage systems
    cookieStorage: createCookieStorage(),
    sessionStorage: createSessionStorage(),

    // Utilities
    log: createLogger(config.bool('logging')),
    dispatchEvent: createEventDispatcher(),

    // Event ID management - Critical for consistent event tracking across platforms
    // CDN scripts use withEcommerceEventContext to ensure all platform calls
    // for the same event (e.g., add_to_cart) share the same eventId
    eventId: createEventIdManager(),
    generateEventId: createGenerateEventId(),
    ecommerceEventId: createEcommerceEventIdManager(),
    getCurrentEcommerceEventId: function () {
      // Direct access to getCurrentEventId for platform integrations
      return (this as any).ecommerceEventId.getCurrentEventId();
    },
    withEcommerceEventContext: function (eventType: string, callback: (eventId: string) => void) {
      // Event context wrapper - generates new ID at start, clears at end
      const eventId = (this as any).ecommerceEventId.startEvent();
      try {
        return callback(eventId);
      } finally {
        (this as any).ecommerceEventId.endEvent();
      }
    },

    // Helper functions expected by CDN scripts
    flattenObject: flattenObject,
    getCartId: getCartId,
    getFirstVariant: getFirstVariant,

    // ═══════════════════════════════════════════════════════════════
    // CRITICAL UTILITY METHODS - Required by CDN scripts
    // ═══════════════════════════════════════════════════════════════

    /**
     * Get URL query parameter value
     * CRITICAL: Used extensively by CDN scripts for URL params like ?variant=123
     * Includes NULL byte sanitization for security
     */
    getQueryParam: (name: string): string | null => {
      if (typeof window === 'undefined') return null;
      if (!name || typeof name !== 'string') return null;

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const value = urlParams.get(name);

        // Sanitize NULL bytes and other control characters for security
        if (value) {
          // Remove NULL bytes (\x00) and other control characters
          return value.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
        }
        return value;
      } catch {
        // Handle malformed URLs gracefully
        return null;
      }
    },

    /**
     * Format price to a consistent numeric format
     * CRITICAL: Used extensively by ALL platform scripts for price calculations
     * @param price - The price value (string, number, or any)
     * @param cents - If true, divides by 100 (converts cents to dollars)
     * @returns Formatted price as a positive number, or 0 if invalid
     */
    formatPrice: (price: any, cents = false): number => {
      try {
        // Handle null or undefined values
        if (price == null) {
          return 0;
        }

        // Use regex to extract numeric values, including decimal points
        const match = price.toString().match(/-?\d+(\.\d+)?/);

        // Convert matched value to a number, or return 0 if no match
        const numericPrice = match ? Number(match[0]) : NaN;

        if (isNaN(numericPrice)) {
          return 0;
        }

        return Math.abs(Number(parseFloat((numericPrice / (cents ? 100 : 1)).toFixed(2))));
      } catch {
        return 0;
      }
    },

    /**
     * Get variant details by ID from variants array
     * CRITICAL: Core variant lookup functionality - must return EXACTLY 6 fields
     * Returns: { id, sku, title, price, compare_at_price, barcode }
     * Price is transformed from cents to dollars (matches extension behavior)
     */
    getVariantDetails: (variants: any[], variantId: string | number): any => {
      try {
        if (!variants || !variantId) return null;

        if (!Array.isArray(variants)) {
          return null;
        }

        // Convert variantId to string for safe comparison
        const variantIdStr = variantId?.toString();

        // Find the variant by id or default to the first variant
        const variant = variantIdStr
          ? variants.find((v) => v.id?.toString() === variantIdStr)
          : variants[0];

        // Return the variant details with ONLY the specific fields CDN expects
        return {
          id: variant?.id?.toString() || null,
          sku: variant?.sku?.toString() || null,
          title: variant?.title?.trim() || null,
          price: (variant?.price || 0) / 100,
          compare_at_price: (variant?.compare_at_price || 0) / 100,
          barcode: variant?.barcode?.toString() || null,
        };
      } catch {
        return {
          id: null,
          sku: null,
          title: null,
          price: 0,
          compare_at_price: 0,
          barcode: null,
        };
      }
    },

    /**
     * Get current selected variant from URL or product data
     */
    getCurrentVariant: function(): any {
      const analyzify = (window as any).analyzify;
      if (!analyzify) return null;

      // Try to get variant ID from URL
      const variantIdFromUrl = analyzify.getQueryParam?.('variant');
      if (variantIdFromUrl && analyzify.product?.variants) {
        const variant = analyzify.getVariantDetails?.(analyzify.product.variants, variantIdFromUrl);
        if (variant) return variant;
      }

      // Fall back to first available variant
      return analyzify.getFirstVariant?.(analyzify.product) || null;
    },

    /**
     * Get latest UTM parameters from history/storage
     * Uses azfy_ prefix to match extension storage keys
     */
    getLatestUtmFromHistory: (): Record<string, string> | null => {
      if (typeof sessionStorage === 'undefined') return null;
      try {
        const utmHistory = sessionStorage.getItem('azfy_utm_history');
        if (utmHistory) {
          const parsed = JSON.parse(utmHistory);
          return Array.isArray(parsed) && parsed.length > 0 ? parsed[parsed.length - 1] : null;
        }
      } catch {
        // Storage error
      }
      return null;
    },

    /**
     * Hash a value using SHA-256
     * Used for privacy-compliant user data hashing
     */
    hashValue: async (value: string): Promise<string> => {
      if (typeof crypto === 'undefined' || !crypto.subtle) {
        // Fallback for environments without crypto.subtle
        return value;
      }
      try {
        const encoder = new TextEncoder();
        const data = encoder.encode(value.toLowerCase().trim());
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      } catch {
        return value;
      }
    },

    /**
     * Hash user data object for privacy compliance
     */
    hashUserData: async (userData: Record<string, any>): Promise<Record<string, any>> => {
      const analyzify = (window as any).analyzify;
      const result: Record<string, any> = {};

      for (const [key, value] of Object.entries(userData)) {
        if (typeof value === 'string' && (key === 'email' || key === 'phone')) {
          result[key] = await analyzify.hashValue?.(value) || value;
        } else {
          result[key] = value;
        }
      }

      return result;
    },

    /**
     * Get effective referrer with fallbacks
     */
    getEffectiveReferrer: (): string => {
      if (typeof document === 'undefined') return '';

      // Try document.referrer first
      if (document.referrer) return document.referrer;

      // Try sessionStorage
      try {
        const storedRef = sessionStorage.getItem('analyzify_referrer');
        if (storedRef) return storedRef;
      } catch {
        // Storage error
      }

      return '';
    },

    /**
     * Get item IDs matching CDN's getItemIds contract.
     * CDN signature: getItemIds({ productObj, product_id_format, variantDetails, eventName, feedRegion })
     * Returns: { selected: string | null, ids: { product_id, variant_id, sku, shopify_id, first_variant } }
     */
    getItemIds: (params: {
      productObj?: any;
      product_id_format?: string;
      variantDetails?: any;
      eventName?: string;
      feedRegion?: string;
    }): { selected: string | null; ids: Record<string, any> } => {
      const { productObj, product_id_format, variantDetails, eventName, feedRegion } = params || {};
      const analyzify = (window as any).analyzify;

      // Cart events use product_id field; regular events use id
      const isCartEvent = eventName === 'view_cart' || eventName === 'begin_checkout';
      const productId = isCartEvent
        ? (productObj?.product_id ?? productObj?.id)
        : (productObj?.id ?? productObj?.product_id);
      const variantId = productObj?.item_variant_id ?? variantDetails?.id ?? productObj?.variant_id ?? productObj?.variants?.[0]?.id;
      const sku = productObj?.item_sku ?? variantDetails?.sku ?? productObj?.sku ?? productObj?.variants?.[0]?.sku;

      const shopifyId = analyzify?.getShopifyId?.(
        feedRegion || analyzify?.feed_region || '',
        productId,
        variantId,
        sku
      ) || null;

      const ids: Record<string, any> = {
        product_id: productId ?? null,
        variant_id: variantId ?? null,
        sku: sku?.toString() ?? null,
        shopify_id: shopifyId,
        first_variant: variantDetails || null,
      };

      // Resolve selected ID based on format, with fallback chain
      const idMap: Record<string, string | null> = {
        product_id: productId?.toString() || null,
        variant_id: variantId?.toString() || null,
        product_sku: sku?.toString() || null,
        shopify_item_id: shopifyId,
      };

      const format = product_id_format || 'product_id';
      const selected = idMap[format]
        || ids.product_id?.toString()
        || ids.sku?.toString()
        || ids.variant_id?.toString()
        || null;

      return { selected, ids };
    },

    /**
     * Find element in event path matching attribute criteria
     * Matches extension behavior with exclusion patterns and element tracking
     */
    findElemInPath: (pathArray: EventTarget[], attributeObj: Record<string, string[]>): Element | null => {
      if (!pathArray || !Array.isArray(pathArray)) return null;
      const analyzify = (window as any).analyzify;

      for (const elem of pathArray) {
        if (!(elem instanceof Element)) continue;

        // Exclusion pattern from extension - skip elements with addon/addition/address in class
        const className = elem.className || '';
        if (typeof className === 'string' && /(addon|addition|address)/i.test(className)) continue;

        for (const [attr, values] of Object.entries(attributeObj)) {
          const elemValue =
            attr === 'class'
              ? elem.className
              : attr === 'href'
                ? elem.getAttribute('href')
                : elem.getAttribute(attr);

          if (elemValue && values.some((v) => elemValue.includes(v))) {
            // Track found elements (matches extension)
            if (analyzify?.foundElements && !analyzify.foundElements.includes(elem)) {
              analyzify.foundElements.push(elem);
            }
            return elem;
          }
        }
      }

      return null;
    },

    /**
     * Get clicked product position in list
     * Searches collection products to find the matching product by href or SKU
     */
    GetClickedProductPosition: (elementHref: string, sku: string): number => {
      if (typeof window === 'undefined') return 1;
      const analyzify = (window as any).analyzify;

      try {
        // Try to get collection products from getCollectionObj
        const collectionObj = analyzify?.getCollectionObj;
        const products = collectionObj?.collection?.products;

        if (!products || !Array.isArray(products)) {
          return 1;
        }

        // Try to find by href (product handle)
        if (elementHref) {
          // Extract handle from href like "/products/my-product" or "https://store.com/products/my-product"
          const handleMatch = elementHref.match(/\/products\/([^\/\?#]+)/);
          if (handleMatch) {
            const handle = handleMatch[1];
            const index = products.findIndex((p: any) => p.handle === handle);
            if (index !== -1) {
              return index + 1; // Return 1-based index
            }
          }
        }

        // Try to find by SKU
        if (sku) {
          const index = products.findIndex((p: any) => {
            // Check product variants for matching SKU
            if (p.variants && Array.isArray(p.variants)) {
              return p.variants.some((v: any) => v.sku === sku);
            }
            // Check collection_sku_list
            const skuList = (window as any).collection_sku_list;
            if (skuList && Array.isArray(skuList)) {
              const skuIndex = skuList.indexOf(sku);
              if (skuIndex !== -1) return skuIndex === products.indexOf(p);
            }
            return false;
          });
          if (index !== -1) {
            return index + 1; // Return 1-based index
          }
        }
      } catch {
        // If anything fails, return default
      }

      return 1;
    },

    /**
     * Get Shopify ID formatted for feed
     * Matches extension format: shopify_{feed}_{productId}_{variantId}
     */
    getShopifyId: (feed: string, itemId: string | number, variantId?: string | number, sku?: string): string | null => {
      try {
        // Missing any required part -> return SKU if available, else null (matches the Analyzify CDN runtime)
        if (!feed || !itemId || !variantId) {
          return sku ? sku : null;
        }
        return `shopify_${feed}_${itemId}_${variantId}`;
      } catch {
        return null;
      }
    },

    /**
     * Get type from HTML tag name
     */
    getTypeFromTag: (tagName: string): string => {
      const tagMap: Record<string, string> = {
        A: 'link',
        BUTTON: 'button',
        INPUT: 'input',
        SELECT: 'select',
        FORM: 'form',
      };
      return tagMap[tagName.toUpperCase()] || 'element';
    },

    // ═══════════════════════════════════════════════════════════════
    // LOCAL STORAGE UTILITIES
    // ═══════════════════════════════════════════════════════════════

    /**
     * Check localStorage size to prevent quota errors
     */
    checkLocalStorageSize: (): number => {
      if (typeof localStorage === 'undefined') return 0;
      let total = 0;
      try {
        for (const key in localStorage) {
          if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
            total += localStorage[key].length * 2; // UTF-16 = 2 bytes per char
          }
        }
      } catch {
        // Storage error
      }
      return total;
    },

    /**
     * Save to localStorage with error handling
     */
    saveToLocalStorage: (key: string, value: any): boolean => {
      if (typeof localStorage === 'undefined') return false;
      try {
        const serialized = typeof value === 'object' ? JSON.stringify(value) : String(value);
        localStorage.setItem(key, serialized);
        return true;
      } catch {
        return false;
      }
    },

    /**
     * Get from localStorage with error handling
     */
    getFromLocalStorage: (key: string): any => {
      if (typeof localStorage === 'undefined') return null;
      try {
        const item = localStorage.getItem(key);
        if (!item) return null;
        try {
          return JSON.parse(item);
        } catch {
          return item;
        }
      } catch {
        return null;
      }
    },

    /**
     * Delete from localStorage
     */
    deleteFromLocalStorage: (key: string): void => {
      if (typeof localStorage === 'undefined') return;
      try {
        localStorage.removeItem(key);
      } catch {
        // Storage error
      }
    },

    /**
     * Reset localStorage key
     */
    resetLocalStorage: (key: string): void => {
      if (typeof localStorage === 'undefined') return;
      try {
        localStorage.removeItem(key);
      } catch {
        // Storage error
      }
    },

    // ═══════════════════════════════════════════════════════════════
    // URL & QUERY UTILITIES
    // ═══════════════════════════════════════════════════════════════

    /**
     * Parse query string into object
     */
    parseQueryString: (queryString: string): Record<string, string> => {
      const result: Record<string, string> = {};
      const params = new URLSearchParams(queryString);
      params.forEach((value, key) => {
        result[key] = value;
      });
      return result;
    },

    /**
     * Replace URL parameter
     */
    urlReplace: (param: string): void => {
      if (typeof window === 'undefined') return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', param);
        window.history.replaceState({}, '', url.toString());
      } catch {
        // URL error
      }
    },

    /**
     * Storage service utility
     */
    storageService: (key: string, value?: any): any => {
      if (typeof key !== 'string') return null;
      if (typeof localStorage === 'undefined') return null;
      try {
        if (value !== undefined) {
          localStorage.setItem(key, value);
          return true;
        }
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },

    /**
     * Parse Shopify attribution cookie
     */
    SSAP_vals: (): Record<string, string> | null => {
      if (typeof document === 'undefined') return null;
      try {
        const match = document.cookie.match(/(?:^|; )_shopify_sa_p=([^;]*)/);
        if (match) {
          return JSON.parse(decodeURIComponent(match[1]));
        }
      } catch {
        // Parse error
      }
      return null;
    },

    /**
     * Find quantity input value from DOM
     */
    findQuantity: (): number => {
      if (typeof document === 'undefined') return 1;
      const quantityInputs = document.querySelectorAll<HTMLInputElement>(
        'input[name="quantity"], input[name="updates[]"], .quantity-selector__input'
      );
      for (const input of quantityInputs) {
        const val = parseInt(input.value, 10);
        if (!isNaN(val) && val > 0) return val;
      }
      return 1;
    },

    /**
     * Check if path segment is present in URL
     */
    isPathSegmentPresent: (segment: string): boolean => {
      if (typeof window === 'undefined') return false;
      return window.location.pathname.includes(segment);
    },

    // Internal state
    addedProduct: null,
    cart_attributes: null,
    sh_info_obj: {},
    itemProps: {},
    gtag_config: {},
    custom_classes: '',
    measurement_id: config.str('ga4_id'),
    measurement_id_v3: '',
    extractedCartDataObj: {}, // Object to store extracted cart data
    unformattedCartDataObj: {}, // For server-side attribution data

    // ═══════════════════════════════════════════════════════════════
    // GA4/GADS PRODUCT FORMATTING FUNCTIONS
    // These functions format product data for GA4 and Google Ads events
    // ═══════════════════════════════════════════════════════════════

    /**
     * Format product ID for Google Ads based on configured format
     * Supports: product_id, variant_id, product_sku, shopify_item_id
     * @param item_id - Product ID
     * @param item_variant_id - Variant ID
     * @param item_sku - Product SKU
     * @returns Formatted ID string for Google Ads
     */
    adsIdFormat: function (
      item_id: string | number | null = null,
      item_variant_id: string | number | null = null,
      item_sku: string | null = null
    ): string {
      const analyzify = (window as any).analyzify;
      const gadsFormat = analyzify?.properties?.GADS?.product_id_format;
      const feedRegion = analyzify?.properties?.GADS?.feed_region || analyzify?.feed_region || 'US';

      // Match ProductIdFormat type: 'product_id' | 'variant_id' | 'product_sku' | 'shopify_item_id'
      if (gadsFormat === 'product_id' && item_id !== null) {
        return item_id.toString();
      } else if (gadsFormat === 'variant_id' && item_variant_id !== null) {
        return item_variant_id.toString();
      } else if (gadsFormat === 'product_sku' && item_sku !== null) {
        return item_sku.toString();
      } else {
        // Default: shopify_item_id format
        return `shopify_${feedRegion}_${item_id}_${item_variant_id}`;
      }
    },

    /**
     * Format single product for GA4/GADS ecommerce events
     * Returns array with single item in GA4 format
     * @param product - Product object with variants
     * @returns Array with single GA4-formatted item
     */
    singleProdInfos: function (product: any): any[] {
      const analyzify = (window as any).analyzify;
      const hasVariants = product.variants && product.variants.length > 0;
      const variant = hasVariants ? product.variants[0] : null;

      const prodInfos = [
        {
          item_id: product.id?.toString() || '',
          item_name: product.title || '',
          item_brand: product.vendor || null,
          item_category: product.type || null,
          item_sku: hasVariants
            ? variant?.sku
            : product.sku || null,
          item_handle: product.handle || '',
          quantity: product.quantity || 1,
          item_variant: hasVariants
            ? variant?.title
            : product.variant_title || null,
          item_variant_id: hasVariants
            ? variant?.id?.toString()
            : product.variant_id?.toString() || null,
          price: Number.parseFloat(
            ((hasVariants ? variant?.price : product.price) / 100).toFixed(2)
          ),
          id: analyzify?.adsIdFormat?.(
            product.id || null,
            (hasVariants ? variant?.id : product.variant_id) || null,
            (hasVariants ? variant?.sku : product.sku) || null
          ) || '',
          google_business_vertical: 'retail',
        },
      ];
      return prodInfos;
    },

    /**
     * Format multiple products for GA4/GADS ecommerce events
     * Used for view_item_list, collection pages
     * @param products - Array of product objects
     * @returns Array of GA4-formatted items
     */
    multipleProdInfos: function (products: any[]): any[] {
      const analyzify = (window as any).analyzify;
      return products.map((product, i) => {
        if (product.title) {
          const hasVariants = product.variants && product.variants.length > 0;
          const variant = hasVariants ? product.variants[0] : null;

          return {
            index: i + 1,
            item_brand: product.vendor || null,
            item_category: product.type || null,
            item_sku: hasVariants ? variant?.sku : product.sku || null,
            item_name: product.title,
            item_handle: product.handle || '',
            item_id: product.id?.toString() || '',
            quantity: product.quantity || 1,
            price: Number.parseFloat(
              ((hasVariants ? variant?.price : product.price || 0) * 0.01).toFixed(2)
            ),
            id: analyzify?.adsIdFormat?.(
              product.id || null,
              product.variant_id || (hasVariants ? variant?.id : null) || null,
              product.sku || null
            ) || '',
            item_variant: hasVariants
              ? variant?.title
              : product.variant_title || null,
            item_variant_id: hasVariants
              ? variant?.id?.toString()
              : product.variant_id?.toString() || '',
            google_business_vertical: 'retail',
          };
        } else {
          return {
            index: i,
            item_name: 'Unknown',
          };
        }
      });
    },

    /**
     * Format cart items for GA4/GADS ecommerce events
     * Used for checkout, view_cart events
     * Cart items have different structure (product_id, product_title, etc.)
     * @param items - Array of cart line items
     * @returns Array of GA4-formatted items
     */
    multipleCartProdInfos: function (items: any[]): any[] {
      const analyzify = (window as any).analyzify;
      return items.map((item, i) => ({
        index: i + 1,
        item_brand: item.vendor || null,
        item_category: item.product_type || null,
        item_sku: item.sku || null,
        item_name: item.product_title || item.title || '',
        item_handle: item.handle || '',
        item_id: item.product_id?.toString() || '',
        quantity: item.quantity || 1,
        price: Number.parseFloat((item.price * 0.01).toFixed(2)),
        id: analyzify?.adsIdFormat?.(
          item.product_id || null,
          item.variant_id || null,
          item.sku || null
        ) || '',
        item_variant: item.variant_title || null,
        item_variant_id: item.variant_id?.toString() || null,
        google_business_vertical: 'retail',
      }));
    },

    /**
     * App start function - signals all platforms are ready
     * Called after platform initialization is complete
     */
    appStart: function (): void {
      const analyzify = (window as any).analyzify;
      if (analyzify) {
        analyzify.initial_load.app_start = true;
        analyzify.log?.('appStart called', 'analyzify-builder');
      }
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// GLOBAL FUNCTIONS - Attached to window (not window.analyzify)
// ═══════════════════════════════════════════════════════════════

/**
 * Initialize global functions required by CDN scripts
 * These are attached to window directly, not window.analyzify
 */
export function initializeGlobalFunctions(): void {
  if (typeof window === 'undefined') return;

  const GTAG_CLIENT_ID_TIMEOUT_MS = 5000;

  /**
   * Get GA4 Client ID from _ga cookie or via gtag callback.
   * Used by server-side tracking for session stitching.
   *
   * Caches to localStorage on every successful retrieval (matches Liquid).
   * No localStorage fallback — Liquid intentionally doesn't read it back
   * (the cache is for other consumers like server-side scripts).
   */
  (window as any).getClientId = async (measurementId?: string): Promise<string | null> => {
    const cacheClientId = (id: string) => {
      try { (window as any).analyzify?.saveToLocalStorage?.('clientId', id); } catch { /* silent */ }
    };

    try {
      // 1. Try _ga cookie first (fastest)
      if (typeof document !== 'undefined') {
        const gaCookie = document.cookie.match(/(?:^|; )_ga=([^;]*)/);
        if (gaCookie) {
          const match = gaCookie[1].match(/GA\d+\.\d+\.(\d+\.\d+)/);
          if (match) {
            (window as any).analyzify?.log?.(`Client ID from cookie: ${match[1]}`, 'global', 'getClientId');
            cacheClientId(match[1]);
            return match[1];
          }
        }
      }

      // 2. Try gtag callback
      if (typeof (window as any).gtag === 'function' && measurementId) {
        return new Promise((resolve) => {
          let settled = false;
          const settle = (value: string | null) => {
            if (settled) return;
            settled = true;
            resolve(value);
          };

          const timeout = window.setTimeout(() => {
            settle(null);
          }, GTAG_CLIENT_ID_TIMEOUT_MS);

          try {
            (window as any).gtag('get', measurementId, 'client_id', (clientId: string) => {
              if (settled) return;
              window.clearTimeout(timeout);
              (window as any).analyzify?.log?.(`Client ID from gtag: ${clientId}`, 'global', 'getClientId');
              if (clientId) cacheClientId(clientId);
              settle(clientId || null);
            });
          } catch {
            window.clearTimeout(timeout);
            settle(null);
          }
        });
      }

      return null;
    } catch (error) {
      (window as any).analyzify?.log?.(error, 'global', 'getClientId');
      return null;
    }
  };

  /**
   * Get GA4 Session ID from _ga_* cookies or via gtag callback.
   * Used by server-side tracking for session stitching.
   *
   * Handles both GS1 and GS2 cookie formats:
   *   GS1: _ga_XXXXX=GS1.1.1234567890.1.0.0.0  → session ID = 1234567890
   *   GS2: _ga_XXXXX=GS2.1.s1234567890.etc      → session ID = 1234567890
   *
   * When measurementId is provided, targets the specific cookie first
   * (_ga_{measurementId minus "G-"}), then falls back to scanning all _ga_* cookies.
   */
  (window as any).getSessionId = async (measurementId?: string): Promise<string | null> => {
    // Liquid's storageService uses localStorage (not sessionStorage).
    // Cache sessionId to localStorage on success; fall back to it when cookies unavailable.
    const CACHE_KEY = 'sessionId';
    const resolveMeasurementId = (): string | null => {
      const isValid = (id: unknown): id is string =>
        typeof id === 'string' && id !== 'null' && id !== 'undefined' && id.startsWith('G-');

      if (isValid(measurementId)) return measurementId;

      return [
        (window as any)?.analyzify_measurement_id,
        (window as any)?.analyzify?.measurement_id,
        (window as any)?.analyzify?.properties?.GA4?.primary?.id,
        (window as any)?.analyzify?.properties?.SERVERSIDE?.measurement_id,
        (window as any)?.analyzify_measurement_id_v3,
      ].find(isValid) || null;
    };
    const cacheSessionId = (id: string) => {
      try {
        const current = localStorage.getItem(CACHE_KEY);
        if (current !== id) localStorage.setItem(CACHE_KEY, id);
      } catch { /* silent */ }
    };

    const getCachedSessionId = (): string | null => {
      try {
        return localStorage.getItem(CACHE_KEY) || null;
      } catch { return null; }
    };

    try {
      /** Extract session ID from a GA4 session cookie value, handling GS1 and GS2 formats. */
      const extractSessionId = (cookieValue: string): string | null => {
        let match: RegExpMatchArray | null = null;
        if (cookieValue.startsWith('GS1')) {
          match = cookieValue.match(/GS1\.\d+\.(\d+)/);
        } else if (cookieValue.startsWith('GS2')) {
          match = cookieValue.match(/GS2\.\d+\.s(\d+)/);
        }
        return match?.[1] ?? null;
      };

      const resolvedMeasurementId = resolveMeasurementId();
      if (!resolvedMeasurementId) {
        (window as any).analyzify?.log?.(
          'No valid measurementId found - unable to retrieve session ID, or measurementId does not start with G-',
          'global',
          'getSessionId',
        );
        return null;
      }

      if (typeof document !== 'undefined') {
        // 1. Try the specific cookie for this measurementId first (targeted lookup)
        const cookieName = `_ga_${resolvedMeasurementId.substring(2)}`;
        const cookieMatch = document.cookie.match(new RegExp(`(?:^|; )${cookieName}=([^;]*)`));
        if (cookieMatch) {
          const sessionId = extractSessionId(decodeURIComponent(cookieMatch[1]));
          if (sessionId) {
            (window as any).analyzify?.log?.(`Session ID from cookie (${cookieName}): ${sessionId}`, 'global', 'getSessionId');
            cacheSessionId(sessionId);
            return sessionId;
          }
        }

        // 2. Fallback: scan all _ga_* cookies
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
          const trimmed = cookie.trim();
          if (trimmed.startsWith('_ga_')) {
            const eqIdx = trimmed.indexOf('=');
            if (eqIdx === -1) continue;
            const value = trimmed.substring(eqIdx + 1);
            const sessionId = extractSessionId(decodeURIComponent(value));
            if (sessionId) {
              (window as any).analyzify?.log?.(`Session ID from cookie: ${sessionId}`, 'global', 'getSessionId');
              cacheSessionId(sessionId);
              return sessionId;
            }
          }
        }
      }

      // 3. Try gtag callback with timeout
      if (typeof (window as any).gtag === 'function') {
        const gtagResult = await new Promise<string | null>((resolve) => {
          const timeout = setTimeout(() => {
            (window as any).analyzify?.log?.('getSessionId timeout', 'global', 'getSessionId');
            resolve(null);
          }, 1000);

          try {
            (window as any).gtag('get', resolvedMeasurementId, 'session_id', (sessionId: string) => {
              clearTimeout(timeout);
              (window as any).analyzify?.log?.(`Session ID from gtag: ${sessionId}`, 'global', 'getSessionId');
              resolve(sessionId || null);
            });
          } catch {
            clearTimeout(timeout);
            resolve(null);
          }
        });

        if (gtagResult) {
          cacheSessionId(gtagResult);
          return gtagResult;
        }
      }

      // 4. Fallback to cached value (matches Liquid's storageService fallback)
      return getCachedSessionId();
    } catch (error) {
      (window as any).analyzify?.log?.(error, 'global', 'getSessionId');
      return null;
    }
  };

  /**
   * Get cookie value by name
   * Used by various CDN scripts for reading tracking cookies
   */
  (window as any).getCookieValue = (cookieName: string): string | null => {
    if (typeof document === 'undefined') return null;
    try {
      const match = document.cookie.match(new RegExp(`(^| )${cookieName}=([^;]+)`));
      return match ? decodeURIComponent(match[2]) : null;
    } catch {
      return null;
    }
  };
}

/**
 * Create cookie storage implementation
 * Note: Method names must match extension (save/delete, not set/remove)
 */
function createCookieStorage() {
  return {
    get: (key: string): string | null => {
      if (typeof document === 'undefined') return null;
      try {
        const match = document.cookie.match(new RegExp(`(^| )${key}=([^;]+)`));
        return match ? decodeURIComponent(match[2]) : null;
      } catch {
        // Handle decode errors gracefully
        return null;
      }
    },
    // Extension uses 'save' not 'set'
    save: (key: string, value: string, maxAge = 2592000): void => {
      if (typeof document === 'undefined') return;
      try {
        const serialized = typeof value === 'object' ? JSON.stringify(value) : value;
        document.cookie = `${key}=${encodeURIComponent(serialized)}; max-age=${maxAge}; path=/; SameSite=Lax`;
      } catch {
        // Storage error
      }
    },
    // Extension uses 'delete' not 'remove'
    delete: (key: string): void => {
      if (typeof document === 'undefined') return;
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    },
    // Extension also has saveWithSizeLimit
    saveWithSizeLimit: (key: string, data: string, maxAge = 2592000): boolean => {
      if (typeof document === 'undefined') return false;
      // Check if cookie exceeds 2048 bytes limit
      if (data.length > 2048) {
        // Delete oversized cookie
        document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        return false;
      }
      document.cookie = `${key}=${encodeURIComponent(data)}; max-age=${maxAge}; path=/; SameSite=Lax`;
      return true;
    },
  };
}

/**
 * Create session storage implementation
 */
function createSessionStorage() {
  return {
    get: (key: string): any => {
      if (typeof sessionStorage === 'undefined') return null;
      try {
        const item = sessionStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch {
        return null;
      }
    },
    save: (key: string, value: any): void => {
      if (typeof sessionStorage === 'undefined') return;
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Storage full or unavailable
      }
    },
    remove: (key: string): void => {
      if (typeof sessionStorage === 'undefined') return;
      sessionStorage.removeItem(key);
    },
  };
}

/**
 * Create logger function
 */
function createLogger(enabled: boolean) {
  return (message: string, file?: string, fn?: string): void => {
    if (!enabled) return;
    const prefix = file ? `[Analyzify:${file}${fn ? `:${fn}` : ''}]` : '[Analyzify]';
    console.log(prefix, message);
  };
}

/**
 * Create event dispatcher function
 * Matches extension behavior with azfy: prefix and Shopify.analytics.publish
 */
function createEventDispatcher() {
  return (name: string, detail?: any): void => {
    if (typeof window === 'undefined') return;

    // Dispatch with azfy: prefix (matches extension)
    const prefixedName = `azfy:${name}`;
    window.dispatchEvent(
      new CustomEvent(prefixedName, {
        detail: { name, payload: detail },
      })
    );

    // Also publish to Shopify.analytics if available
    if (typeof (window as any).Shopify?.analytics?.publish === 'function') {
      (window as any).Shopify.analytics.publish(prefixedName, { name, payload: detail });
    }
  };
}

/**
 * Create event ID manager
 */
function createEventIdManager() {
  const eventIds: Record<string, string> = {};

  return {
    get: function (eventType = 'default'): string {
      if (!eventIds[eventType]) {
        eventIds[eventType] = (window as any).analyzify?.generateEventId?.('sh', '', 'z9y8x7w6v5u4t3s2') ?? crypto.randomUUID();
      }
      return eventIds[eventType];
    },
    reset: function (eventType = 'default'): string {
      eventIds[eventType] = (window as any).analyzify?.generateEventId?.('sh', '', 'z9y8x7w6v5u4t3s2') ?? crypto.randomUUID();
      return eventIds[eventType];
    },
    resetAll: function (): void {
      for (const key in eventIds) {
        delete eventIds[key];
      }
    },
  };
}

/**
 * Create ConsentManager - queue system for consent-dependent operations
 *
 * CDN scripts use this to queue actions until consent is determined.
 * Matches the structure from the Analyzify consent layer
 */
/**
 * Create generateEventId function matching extension implementation
 * Generates UUID-like identifier with optional salt influence
 */
function createGenerateEventId() {
  return (prefix = 'sh', separator = '-', salt = ''): string => {
    // Generate 5 segments: [8, 4, 4, 4, 12] hex chars each
    const segments = [8, 4, 4, 4, 12];
    const hexChars = '0123456789abcdef';

    const generateSegment = (length: number, saltOffset: number): string => {
      let segment = '';
      for (let i = 0; i < length; i++) {
        let randomIndex = Math.floor(Math.random() * 16);
        // Apply salt if provided
        if (salt && salt.length > 0) {
          const saltCharCode = salt.charCodeAt((saltOffset + i) % salt.length);
          randomIndex = (randomIndex + saltCharCode) % 16;
        }
        segment += hexChars[randomIndex];
      }
      return segment;
    };

    let saltOffset = 0;
    const parts = segments.map((segmentLength) => {
      const segment = generateSegment(segmentLength, saltOffset);
      saltOffset += segmentLength;
      return segment;
    });

    return `${prefix}${separator}${parts.join(separator)}`;
  };
}

/**
 * Create EcommerceEventId manager matching extension implementation
 * State machine for transaction context tracking - ensures all platform calls
 * for the same event (add_to_cart, remove_from_cart, begin_checkout) share the same eventId
 */
function createEcommerceEventIdManager() {
  let currentEventId: string | null = null;
  let eventCounter = 0;

  // Get reference to generateEventId (will be available on window.analyzify)
  const generateEventId = createGenerateEventId();

  return {
    /**
     * Generates new event ID and starts a new event context
     * Uses Date.now() + counter as salt for uniqueness
     */
    startEvent: function (): string {
      eventCounter++;
      currentEventId = generateEventId('sh', '', `${Date.now()}${eventCounter}`);
      return currentEventId;
    },

    /**
     * Returns current event ID or generates new one if none exists
     * CDN scripts call this to retrieve the ID created in withEcommerceEventContext
     */
    getCurrentEventId: function (): string {
      return currentEventId || this.startEvent();
    },

    /**
     * Clears current event ID - called after event context completes
     */
    endEvent: function (): void {
      currentEventId = null;
    },

    /**
     * Backward compatibility alias for getCurrentEventId
     */
    get: function (): string {
      return this.getCurrentEventId();
    },
  };
}

/**
 * Flatten nested object to dot notation
 * Used by CDN scripts for data transformation
 */
function flattenObject(
  obj: Record<string, any>,
  parentKey = '',
  result: Record<string, any> = {}
): Record<string, any> {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        flattenObject(obj[key], newKey, result);
      } else {
        result[newKey] = obj[key];
      }
    }
  }
  return result;
}

/**
 * Get cart ID safely with fallback
 */
function normalizeCartToken(rawCartId: unknown): string | null {
  if (typeof rawCartId !== 'string' || rawCartId.length === 0) return null;
  const withoutGid = rawCartId.replace(/^gid:\/\/shopify\/Cart\//, '');
  const token = withoutGid.split('?')[0];
  return token || withoutGid;
}

function getCartId(): string | null {
  if (typeof window === 'undefined') return null;
  const analyzify = (window as any).analyzify;
  if (!analyzify) return null;

  try {
    const cartCookie = analyzify.cookieStorage?.get?.('cart');
    if (typeof cartCookie === 'string' && cartCookie) {
      const trimmed = cartCookie.trim();

      if (trimmed.startsWith('{')) {
        try {
          return normalizeCartToken(JSON.parse(trimmed)?.token);
        } catch {
          return null;
        }
      }

      return normalizeCartToken(trimmed);
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * Get first available variant from product
 * CRITICAL: Must return EXACTLY 6 fields to match CDN expectations
 * Returns: { id, price, compare_at_price, barcode, title, sku }
 */
function getFirstVariant(product: any): any {
  try {
    if (!product) return null;

    // Handle Storefront API format (edges)
    let firstVariant: any = null;
    if (product.variants?.edges && product.variants.edges.length > 0) {
      firstVariant = product.variants.edges[0]?.node;
    } else if (Array.isArray(product.variants) && product.variants.length > 0) {
      firstVariant = product.variants[0];
    }

    // Return exactly the structure CDN expects with correct fallback logic
    return {
      id: product?.variant_id || firstVariant?.id || null,
      price: product?.price || firstVariant?.price || null,
      compare_at_price: product?.compare_at_price || firstVariant?.compare_at_price || null,
      barcode: firstVariant?.barcode || null,
      title: product?.variant_title || firstVariant?.title || null,
      sku: product?.sku || firstVariant?.sku || null,
    };
  } catch {
    return null;
  }
}

function buildStoreObj(shopDomain?: string): { store_url: string; store_name: string } {
  if (!shopDomain) return { store_url: '', store_name: '' };
  const domain = shopDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return {
    store_url: `https://${domain}`,
    store_name: domain.replace('.myshopify.com', ''),
  };
}

function createConsentManager() {
  type ConsentCallback = (consent: any) => void;

  const manager = {
    isInitialized: false,
    consentReady: false,
    hasCMP: false,
    consentQueue: [] as ConsentCallback[],

    /**
     * Queue an action to run when consent is ready
     * If consent is already ready, runs immediately
     */
    queueConsentAction(callback: ConsentCallback): void {
      if (this.consentReady) {
        const currentConsent = (window as any).analyzify?.current_consent;
        callback(currentConsent);
      } else {
        this.consentQueue.push(callback);
      }
    },

    /**
     * Process all queued actions when consent becomes ready
     */
    processConsentQueue(): void {
      if (!this.consentReady) this.consentReady = true;
      const currentConsent = (window as any).analyzify?.current_consent;

      while (this.consentQueue.length > 0) {
        const callback = this.consentQueue.shift();
        if (callback) {
          try {
            callback(currentConsent);
          } catch (error) {
            console.error('Error processing queued consent action:', error);
          }
        }
      }
    },

    /**
     * Dispatch consent update event
     */
    dispatchUpdate(consent: any, status: string): void {
      if (typeof document !== 'undefined') {
        // Liquid's { consent, status } shape (the Analyzify consent layer) with the GCM
        // fields also kept flat — onChange subscribers and an_gtm.js read
        // detail.consent / detail.status, other listeners read detail.ad_storage.
        document.dispatchEvent(
          new CustomEvent('AnalyzifyConsent', {
            detail: buildAnalyzifyConsentDetail(consent, status),
          })
        );

        // Push consentUpdate to GTM dataLayer.
        // In Liquid themes, the Analyzify consent layer listens for AnalyzifyConsent and pushes
        // this to dataLayer. In headless, that snippet isn't loaded. This is the
        // CMP-agnostic consent signal that GTM Trigger Groups use.
        const win = window as any;
        if (win.dataLayer && win.analyzify?.gcm_active) {
          const consentSettings = {
            ...consent,
            analyzify_consent: (
              consent?.ad_storage === 'granted' &&
              consent?.analytics_storage === 'granted'
            ) ? 'granted' : 'denied',
          };
          win.dataLayer.push({
            event: 'consentUpdate',
            status: status,
            consentSettings: consentSettings,
          });
        }

        (window as any).analyzify?.log?.('Consent updated.', 'consentManager', 'dispatchUpdate');
      }
    },

    /**
     * Register callback for consent changes.
     *
     * One document listener serves every subscriber: a listener per
     * subscriber would be a byte-identical closure each time, which the
     * SDK's listener de-duplication (repeated CDN platform init) could not
     * tell apart from a duplicate.
     */
    changeCallbacks: [] as ConsentCallback[],
    _listening: false,
    onChange(callback: ConsentCallback): void {
      this.changeCallbacks.push(callback);
      if (this._listening || typeof document === 'undefined') return;
      this._listening = true;
      document.addEventListener('AnalyzifyConsent', (e: Event) => {
        const detail = (e as CustomEvent).detail;
        // Liquid shape { consent, status }, or flat GCM fields
        const consent = detail?.consent || (detail?.ad_storage ? detail : null);
        if (!consent) return;
        for (const cb of this.changeCallbacks) {
          try {
            cb(consent);
          } catch (error) {
            console.warn('[ConsentManager] Error in consent change callback:', error);
          }
        }
        (window as any).analyzify?.log?.(
          'AnalyzifyConsent event received.',
          'consentManager',
          'onChange'
        );
      });
    },
  };

  return manager;
}
